import type { InternalAxiosRequestConfig } from "axios";
import { createAxiosInstance, getAuthToken } from "@/dataProvider/axios";
import { AGENTIC_BASE_URL } from "@/services/agentic";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { getDefaultOrgId } from "@/utils/defaultOrg";

export const DEV_AGENT_BASE_URL = AGENTIC_BASE_URL;

function devAgentRequestUrl(cfg: InternalAxiosRequestConfig): string {
  const base = cfg.baseURL ?? "";
  const path = cfg.url ?? "";
  return path.startsWith("http") ? path : `${base}${path}`;
}

function logDevAgentRequest(config: InternalAxiosRequestConfig): void {
  const rawAuth =
    config.headers?.Authorization ??
    (config.headers as Record<string, unknown> | undefined)?.authorization;
  const authStr = typeof rawAuth === "string" ? rawAuth : "";
  const tokenLen = authStr.startsWith("Bearer ") ? authStr.length - 7 : 0;
  console.log("[dev-agent HTTP] request", {
    method: (config.method || "get").toUpperCase(),
    url: devAgentRequestUrl(config),
    hasBearerToken: tokenLen > 0,
    tokenCharLength: tokenLen,
  });
}

function logDevAgent401(error: unknown): void {
  const err = error as {
    response?: { status?: number; data?: unknown };
    config?: InternalAxiosRequestConfig;
  };
  if (err?.response?.status !== 401 || !err.config) return;
  console.warn("[dev-agent HTTP] 401 Unauthorized", {
    method: (err.config.method || "get").toUpperCase(),
    url: devAgentRequestUrl(err.config),
    responseBody: err.response?.data,
  });
}

export const api = createAxiosInstance(DEV_AGENT_BASE_URL);

api.interceptors.request.use((config) => {
  logDevAgentRequest(config);
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    logDevAgent401(error);
    if (error?.response?.status === 401) {
      import("../errorHandler").then(({ handleError }) =>
        handleError(error, "session"),
      );
    }
    return Promise.reject(error);
  },
);

export function unwrap<T>(data: any): T {
  return data?.data && data?.status ? (data.data as T) : (data as T);
}

export function currentDevAgentUserId(): string {
  return useAuthStoreV1.getState().user?.id || "";
}

function readBrowserOrgId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem("org_id");
    if (stored) return stored;
    const cookie = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith("org_id="));
    return cookie ? decodeURIComponent(cookie.slice("org_id=".length)) : null;
  } catch {
    return null;
  }
}

function resolveCurrentOrgId(state: ReturnType<typeof useAuthStoreV1.getState>): string | null {
  if (state.selectedOrg?.id) return state.selectedOrg.id;
  const defaultOrgId = getDefaultOrgId();
  if (defaultOrgId) return defaultOrgId;
  const legacyOrgId = readBrowserOrgId();
  if (legacyOrgId) return legacyOrgId;
  return state.organizations.length === 1 ? state.organizations[0].id : null;
}

/**
 * Same header-builder pattern as mcp-gateway/skills/git — resolves org id
 * (selectedOrg → default org → localStorage/cookie → single-org fallback)
 * and user id (arg → store), and sends both as headers so the backend can
 * scope the request without relying solely on the query-param convention
 * that `withUserId`/`withIdentity` use below.
 */
export function devAgentAuthHeaders(
  orgId?: string | null,
  userId?: string | null,
): Record<string, string> {
  const state = useAuthStoreV1.getState();
  const effectiveOrgId = orgId || resolveCurrentOrgId(state);
  const effectiveUserId = userId || state.user?.id || null;
  const headers: Record<string, string> = {};
  if (effectiveOrgId) headers["X-Organization-ID"] = effectiveOrgId;
  if (effectiveUserId) headers["X-User-ID"] = effectiveUserId;
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export function withUserId(path: string, userIdOverride?: string): string {
  const userId = userIdOverride || currentDevAgentUserId();
  if (!userId) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}user_id=${encodeURIComponent(userId)}`;
}

export function withIdentity(path: string, userId?: string, orgId?: string): string {
  let next = withUserId(path, userId);
  if (!orgId) return next;
  const separator = next.includes("?") ? "&" : "?";
  return `${next}${separator}org_id=${encodeURIComponent(orgId)}`;
}

export function createDevAgentApiInstance() {
  const instance = createAxiosInstance(DEV_AGENT_BASE_URL);
  instance.interceptors.request.use((config) => {
    logDevAgentRequest(config);
    return config;
  });
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      logDevAgent401(error);
      return Promise.reject(error);
    },
  );
  return instance;
}
